import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { DayOfWeek, FirstWeekOfYear, IDatePickerStrings } from '@fluentui/react'
import { DatePicker, DatePickerProps } from '@fluentui/react-datepicker-compat'
import { formatFullNumericDate, formatIsoDate, parseDate } from '../../helpers/dateHelper'
import { useTranslation } from 'react-i18next'

export type LocalDatePickerProps = Omit<DatePickerProps, 'value' | 'onSelectDate' | 'placeholder'> & {
    isoValue?: string
    onSelectDate?: (isoDate: string) => void
}

export const dayPickerStrings: IDatePickerStrings = {
    months: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
    shortMonths: ['Jan', 'Feb', 'Maa', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
    days: ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
    shortDays: ['Z', 'M', 'D', 'W', 'D', 'V', 'Z'],
    goToToday: 'Ga naar vandaag',
    prevMonthAriaLabel: 'Ga naar vorige maand',
    nextMonthAriaLabel: 'Ga naar volgende maand',
    prevYearAriaLabel: 'Ga naar vorig jaar',
    nextYearAriaLabel: 'Ga naar volgend jaar',
}

export const defaultDatePickerProps: Pick<
    DatePickerProps,
    | 'showWeekNumbers'
    | 'firstDayOfWeek'
    | 'firstWeekOfYear'
    | 'showMonthPickerAsOverlay'
    | 'placeholder'
    | 'strings'
    | 'allowTextInput'
    | 'disableAutoFocus'
> = {
    showWeekNumbers: true,
    firstDayOfWeek: DayOfWeek.Monday,
    firstWeekOfYear: FirstWeekOfYear.FirstDay,
    showMonthPickerAsOverlay: false,
    placeholder: 'base.datepicker.placeholder',
    strings: dayPickerStrings,
    allowTextInput: true,
    disableAutoFocus: true,
}

export default function LocalDatePicker(props: LocalDatePickerProps): ReactElement {
    const { t: translate } = useTranslation()

    const [value, setValue] = useState<Date>(null)

    useEffect(() => {
        setValue(props.isoValue === null ? null : parseDate(props.isoValue)?.toBSON())
    }, [props.isoValue])

    const allProps = { ...defaultDatePickerProps, ...props }

    return (
        <DatePicker
            formatDate={formatFullNumericDate}
            parseDateFromString={(date: string) => DateTime.fromFormat(date, 'd-M-yy').toBSON()}
            {...allProps}
            placeholder={translate(allProps.placeholder)}
            value={value}
            onSelectDate={(date: Date) => props.onSelectDate?.(date === null ? null : formatIsoDate(date))}
            onChange={() => {}}
        />
    )
}
